// 치과 「오늘」 — 도착 예정을 한 화면에 (2026-08-11 대표 확정, 2026-09-29 무접촉 공정 흐름으로 개정)
// 시안: ~/Downloads/clinic_today_merged_preview.html
//
// 기준은 **기공물 도착 예정일**(의뢰서에 이미 적는 날짜) — 새로 입력할 칸을 만들지 않는다.
// 2026-09-29 대표 확정(버튼 0개): [도착완료]·[되돌리기] 버튼 없음. 오늘·내일 예정 건만 보여주고
//  예정일이 지나면 자동으로 목록에서 내려간다(서버가 납기 자정에 auto_due로 완료 처리). 안 온 기공물은 전화·카톡(현행 관행).
//  월말 대조의 근거는 도착 표시가 아니라 **거래명세서 확인**이다. 자동 처리는 편의 표시이며 납품·수령을 증명하지 않는다.
// 데이터: workOrders(치과가 보낸 의뢰서) 하나. 과거 수동 기록 receivedAt/receivedBy/receivedByName는 읽기만 한다(새로 쓰지 않음).
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import RemakeKindModal from './common/RemakeKindModal';
import StlViewer from './StlViewer';
import ClinicProcessCalendar from './ClinicProcessCalendar';
import { remakeLabelOf, remakeBadgeColors } from '../utils/remakeKind';
import { clinicStageOf, STAGE3_COLOR } from '../utils/shared/clinic-stage';   // 도착 예정 칩 공정 표시(대표 10-13 "치과도 상태만 보이게")
import { Sun, Ticket, Search, ClipboardList, FilePlus } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { getFeatures, isBetaUser, isClinicScanIntakeOn, isClinicWalkinOn, isPatientFeedbackOn } from '../utils/features';
import ClinicIntakeCard from './clinic/ClinicIntakeCard';   // 🩺 환자앱 첫 방문 등록 수신 카드 (2026-09-30, 플래그 patientIntake ON일 때만 렌더=호출)
import ClinicWalkinCard from './clinic/ClinicWalkinCard';   // 🪪 웹 접수 "오늘 접수 N건" 카드 (2026-10-01, 플래그 clinicWalkin(단독) ON일 때만 마운트=호출)
import ClinicFeedbackNeedCard from './clinic/ClinicFeedbackNeedCard';   // 🩺 "장착 후 확인 — 확인 필요 N건" 카드 (2026-10-02, 플래그 patientFeedback strict ON일 때만 마운트=호출, 0건이면 숨김)
import ClinicSelfRegisterCard from './clinic/ClinicSelfRegisterCard';   // 🪪 「새 앱 등록 N명」 알림 (덴클 케어 W3, 2026-10-04 — 확인 버튼 없음, careApp 플래그를 카드가 직접 보고 OFF·0건이면 숨김)

const DAY = 24 * 3600 * 1000;

const toDate = (t) => {
    if (!t) return null;
    if (t.toDate) return t.toDate();
    if (t instanceof Date) return t;
    const d = new Date(t);
    return isNaN(d) ? null : d;
};
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// 도착 예정일 — 의뢰서의 납기(도착 희망일). 구건은 문자열 deliveryDate만 있는 경우가 있다
const arriveOf = (o) => toDate(o.dueDate) || toDate(o.deliveryDate);

function ClinicToday({ user }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [error, setError] = useState('');
    const [remakePick, setRemakePick] = useState(null);   // [리메이크] 선택 모달 대상 의뢰서 (재제작/수리)
    const [preview3d, setPreview3d] = useState(null);     // [3D] 디자인 미리보기 모달 { entries, title, notice } (2026-09-04 대표 확정 B안)
    // A/S 재제작 상담 배너 노출 판정용 (기공소 대시보드와 동일 규칙)
    const [featureFlags, setFeatureFlags] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatureFlags(f); }).catch((e) => { console.error('[오늘] 플래그 조회 실패(배너·스캔 칩 생략):', e?.message || e); });
        return () => { alive = false; };
    }, []);

    const uid = user?.companyId || user?.uid;

    const load = useCallback(async () => {
        if (!uid) return;
        setLoading(true);
        setError('');
        try {
            // 조회 창: createdAt 최근 90일 (복합 색인 fromUserId+createdAt) — 전량 조회 금지 (2026-09-29 Z1)
            const since = Date.now() - 90 * DAY;
            const snap = await getDocs(query(
                collection(db, 'workOrders'),
                where('fromUserId', '==', uid),
                where('createdAt', '>=', Timestamp.fromMillis(since)),
                orderBy('createdAt', 'desc'),
            ));
            setOrders(snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(o => o.source !== 'agent_auto' && !['cancelled', 'canceled', 'rejected'].includes(o.status || '')));   // 거절 건 제외 (09-03 점검 #1)
        } catch (e) {
            console.error('[오늘] 조회 실패:', e);
            setError('의뢰서를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            setLoading(false);
        }
    }, [uid]);

    useEffect(() => { load(); }, [load]);

    // 기공소 감지 케이스 조회(CF getClinicCaseProgress)는 2026-10-12 「작업 공정」 숫자 요약 삭제(대표 "권고대로", 앱 1.3.0과 동일)로
    //  쓰는 곳이 없어져 호출을 뺐다 — 칩 표시는 2026-09-29에 이미 제거됨. 캘린더는 의뢰서(orders)만 그린다.
    const [qText, setQText] = useState('');   // 환자명 검색 (2026-09-03 대표 요청 "치과쪽도")
    const qNorm = qText.trim().toLowerCase().replace(/\s/g, '');
    const hitQ = (name) => !qNorm || (name || '').toString().toLowerCase().replace(/\s/g, '').includes(qNorm);

    // 스캔만 된 환자 (2026-09-02 대표 지적 "구강스캐너로 스캔된 환자가 작업 공정에 안 뜬다"):
    //  덴클 프로그램이 감지했지만 아직 의뢰서가 없는 scans 문서 — 「스캔」 칸에 '의뢰서 작성' 칩으로 보여준다.
    //  최근 7일(대표 지시 09-02 — 30일은 너무 많음)·의뢰서 연결 안 된 것만. 실패는 콘솔에 남기고 화면은 의뢰서만으로 그린다.
    const [scanOnly, setScanOnly] = useState([]);        // 보이는 것 (숨김 제외)
    const [scanHidden, setScanHidden] = useState([]);    // 숨긴 것 (dismissedAt) — [보기]로 되살릴 수 있다
    const [showHiddenScans, setShowHiddenScans] = useState(false);
    const [showScanFold, setShowScanFold] = useState(false);   // 요약 줄 옆 '스캔만 N건 ▸' 접기 (캘린더 전환 후, 2026-09-29)
    const scanNameOf = (sc) => sc.parsedOrder?.patientName || sc.patientNameGuess || sc.patientName || sc.caseLabel || '환자 미상';
    // 숨김/되살리기 — 삭제가 아니라 표시(dismissedAt)만 기록 (삭제하면 프로그램 수동 감지 때 재등록됨, 09-02 대표 확정)
    const toggleScanHidden = async (sc, hide) => {
        try {
            await updateDoc(doc(db, 'scans', sc.id), { dismissedAt: hide ? serverTimestamp() : null, updatedAt: serverTimestamp() });
            if (hide) { setScanOnly(prev => prev.filter(x => x.id !== sc.id)); setScanHidden(prev => [{ ...sc, dismissedAt: new Date() }, ...prev]); }
            else { setScanHidden(prev => prev.filter(x => x.id !== sc.id)); setScanOnly(prev => [{ ...sc, dismissedAt: null }, ...prev]); }
        } catch (e) {
            console.error('[오늘] 스캔 숨김 실패:', e);
            alert('처리하지 못했습니다.\n' + (e?.message || '잠시 후 다시 시도해 주세요.'));
        }
    };
    // 스캔 수집 플래그 OFF면 스캔 칩을 그리지 않는다 — 도착지(의뢰서 작성)가 ?scanId=를 무시해 조용히 끊기던 것 방지 (09-03 점검 #3)
    const scanIntakeOn = !!featureFlags && isClinicScanIntakeOn(featureFlags, user, user?.email);
    useEffect(() => {
        if (!uid || !scanIntakeOn) { setScanOnly([]); setScanHidden([]); return; }
        (async () => {
            try {
                const snap = await getDocs(query(collection(db, 'scans'), where('clinicId', '==', uid), orderBy('detectedAt', 'desc'), limit(150)));
                const since = Date.now() - 7 * DAY;
                const all = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(sc => {
                    if (sc.orderId || sc.linkedOrderId) return false;
                    if (!['detected', 'uploading', 'ready', 'delivered'].includes(sc.status)) return false;
                    const t = toDate(sc.scannedAt || sc.detectedAt);
                    return t && t.getTime() > since;
                });
                setScanOnly(all.filter(sc => !sc.dismissedAt));
                setScanHidden(all.filter(sc => !!sc.dismissedAt));
            } catch (e) {
                console.error('[오늘] 스캔 목록 조회 실패(의뢰서만 표시):', e?.message || e);
            }
        })();
    }, [uid, scanIntakeOn]);

    // (구) 의뢰서에 연결된 scans 문서 읽기(scanMap)는 2026-10-07 2상태 단순화로 제거 — 스캔 전송 상태는 치과 화면에 더 이상 표시하지 않는다.

    // [도착완료]/[되돌리기] 기록 함수(markReceived)는 2026-09-29 무접촉 공정 흐름으로 제거 — 도착 표시는 사람이 누르지 않는다.

    // 리메이크 작성 — 지난 의뢰서를 그대로 불러와 새 의뢰서로 시작한다.
    // 환자·기공소·치식·재료·쉐이드가 채워진 채로 열리고, 재제작 표시와 원 의뢰 연결도 자동으로 붙는다.
    // 2026-09-04 대표 승인: 누르면 "재제작 / 수리" 선택 모달(예정일 전 경고 문구는 모달 본문에 유지) → 고른 값이 remakeKind로 실린다.
    const goRemake = (o) => setRemakePick(o);

    // 디자인 미리보기 — 기공소 에이전트가 올린 designPreview.files(치식별 GLB/STL). 완료 처리 시 서버가 삭제한다.
    const open3d = (o) => {
        const files = (o.designPreview?.files || []).filter(f => f?.url);
        if (files.length === 0) { alert('열 수 있는 미리보기 파일이 없습니다.'); return; }
        setPreview3d({
            entries: files.map(f => ({ url: f.url, path: f.path, name: (f.path || '').split('/').pop(), tooth: f.tooth, kind: f.kind, format: f.format, size: f.size, reduced: !!f.reduced, layer: f.layer || null, part: f.part || null })),
            title: `디자인 미리보기 · ${o.patientName || ''}`,
            notice: '미리보기는 디자인 확인용이며 최종 보철과 다를 수 있습니다.',
        });
    };
    const goRemakeWithKind = (o, remakeKind) => {
        setRemakePick(null);
        // 주문 문서를 통째로 싣지 않는다 — 필요한 값만 (환자 정보가 브라우저 기록에 남지 않게)
        const pick = {
            id: o.id, patientName: o.patientName || '', patientGender: o.patientGender || '',
            patientAge: o.patientAge || '', toUserId: o.toUserId || '', isInternalLab: !!o.isInternalLab,
            items: o.items || [], prosthesisGroups: o.prosthesisGroups || [], bridgeGroups: o.bridgeGroups || [],
            ponticGroups: o.ponticGroups || [], implantGroups: o.implantGroups || [],
            specialNotes: o.specialNotes || '', createdAt: o.createdAt || null,
            isRemake: !!o.isRemake, remakeOfOrderId: o.remakeOfOrderId || '',
            remakeKind,   // 'remake' | 'repair'
        };
        navigate('/create-order', { state: { remakeFrom: pick } });
    };

    const groups = useMemo(() => {
        const today = startOfDay(new Date()).getTime();
        const tomorrow = today + DAY;
        const waiting = orders.filter(o => !o.receivedAt);
        const noDate = waiting.filter(o => !arriveOf(o));

        // 도착 예정 칩은 '아직 안 받은'(waiting) 건 기준 — 과거 수동 기록(receivedAt)이 있는 건은 제외.
        // 예정일이 지난 건(late)은 화면에 표시하지 않는다(2026-09-29 대표 확정 — 자동으로 목록에서 내려감, 서버가 납기 자정에 완료 처리).
        const todayList = waiting.filter(o => { const a = arriveOf(o); return a && startOfDay(a).getTime() === today; });
        // 내일 도착 예정 — 기공소 대시보드의 '내일 마감'에 대응 (치과 관점 = 도착 예정)
        const tomorrowList = waiting.filter(o => { const a = arriveOf(o); return a && startOfDay(a).getTime() === tomorrow; });

        // 「작업 공정」 3단계 숫자 요약(의뢰서 확인·디자인 완료·포장 완료)은 2026-10-12 대표 "권고대로" 삭제 — 앱 1.3.0과 동일.

        const byDate = (a, b) => (arriveOf(a)?.getTime() || 0) - (arriveOf(b)?.getTime() || 0);

        return {
            today: todayList.sort(byDate),
            tomorrow: tomorrowList.sort(byDate),
            noDate,
        };
    }, [orders]);

    if (loading) return (
        <div style={S.page}><PageHeader icon={Sun} title="기공 캘린더" /><div style={S.empty}>불러오는 중…</div></div>
    );

    // A/S 재제작 상담 배너 노출 — 4차(매칭) 오픈 또는 베타만. 플래그 미로드 시 안전하게 숨김
    const matchOpen = !!featureFlags && (isBetaUser(featureFlags, user) || ((featureFlags?.phase || 1) >= 4));
    // 시작 가이드 — 아직 의뢰서가 하나도 없는(빈 화면) 치과에만
    const isEmpty = orders.length === 0;

    return (
        <div style={S.page}>
            <PageHeader icon={Sun} title="기공 캘린더" />

            {/* 🎁 A/S 재제작 상담 배너 — 2026-10-07 대표 지시로 기공 캘린더에서 제거(메뉴 '기공 관리 › A/S 재제작 상담'으로 충분). 블록 보존 */}
            {false && matchOpen && (
                <div
                    onClick={() => navigate('/as-remake')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                        background: 'linear-gradient(135deg, #ECFDF5 0%, #EFF6FF 100%)',
                        border: '1px solid #A7F3D0', borderRadius: 12, padding: '16px 20px',
                        marginBottom: 16, cursor: 'pointer',
                    }}
                >
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Ticket size={22} color="#fff" />
                    </div>
                    <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#065F46' }}>A/S 재제작 상담</div>
                        <div style={{ fontSize: 13, color: '#374151', marginTop: 2, lineHeight: 1.6 }}>
                            덴탈클러스터 의뢰서 기반으로 A/S 보철 재제작을 상담하고, 실력 있는 새 기공소를 만나보세요.
                        </div>
                    </div>
                    <button
                        onClick={(e) => { e.stopPropagation(); navigate('/as-remake'); }}
                        style={{ background: '#059669', color: '#fff', border: 'none', padding: '11px 18px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0 }}
                    >
                        A/S 보철 의뢰하기
                    </button>
                </div>
            )}

            <div style={S.topbar}>
                {/* 상단 설명 문구("도착 예정일 순서…")는 2026-10-07 대표 승인(단순화)으로 제거 — 안내는 달력 하단 한 줄만 */}
                <div style={S.topText} />
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                        <input value={qText} onChange={(e) => setQText(e.target.value)} placeholder="환자명 검색" aria-label="환자명 검색"
                            style={{ width: 150, padding: '7px 24px 7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
                        {qText && <button onClick={() => setQText('')} title="지우기" style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, padding: 2 }}>×</button>}
                    </div>
                    <button onClick={load} style={S.chip}>새로 고침</button>
                    <button onClick={() => navigate('/create-order')} style={S.btnP}>+ 의뢰서 작성</button>
                </div>
            </div>

            {error && <div style={S.error}>{error}</div>}

            {/* 🩺 첫 방문 등록 카드 — config/features.patientIntake === true 일 때만 마운트(OFF면 listClinicIntakes 호출 자체가 없다). 베타 미리보기 없음(계약 §5) */}
            {/* 플래그와 무관하게 마운트 — 기능을 끈 뒤에도 남은 전달본(옮겨 적기 대기)은 처리해야 한다(검토 P1-3, 2026-09-30). 카드는 0건이면 스스로 숨는다. 제출만 서버가 플래그로 막는다 */}
            {/* 🪪 웹 접수 목록 — config/features.clinicWalkin === true(단독) 일 때만 마운트(OFF면 listClinicWalkins 호출 자체가 없다). 첫 방문 등록 카드 위 (2026-10-01) */}
            {isClinicWalkinOn(featureFlags) && <ClinicWalkinCard clinicId={uid} />}
            {/* 🪪 새 앱 등록 N명 — 2026-10-06 대표 지시로 「오늘」에서 제거(앱 등록은 즉시 성립이라 치과가 할 일이 없음 — 환자 관리 명부의 「앱 등록」 표시로 충분). 컴포넌트는 보존 */}
            {false && <ClinicSelfRegisterCard clinicId={uid} />}
            <ClinicIntakeCard />
            {/* 🩺 장착 후 확인 — config/features.patientFeedback === true(strict) && 환자관리 열림일 때만 마운트(OFF면 listClinicFeedbacks 호출 0). 0건이면 스스로 숨는다 */}
            {!!featureFlags && isPatientFeedbackOn(featureFlags, user, user?.email) && <ClinicFeedbackNeedCard />}

            {/* 예정일 지난 건 경고 띠 제거 (2026-09-29 대표 확정 — 예정일이 지나면 자동으로 내려가고, 안 온 기공물은 전화·카톡) */}
            {/* 상단 건수 타일 제거 (2026-08-14 대표 지시 — 아래 도착 예정 칩 카드가 실제 환자를 보여줘 중복).
                groups.today/tomorrow 계산은 도착 예정 칩 카드에서 계속 사용 */}

            {/* 🚀 신규 치과 시작 가이드 — 아직 의뢰서가 없을 때 (기공소 대시보드에서 이식) */}
            {isEmpty && (
                <div style={S.guide}>
                    <div style={S.guideTitle}>덴탈클러스터 시작하기</div>
                    <div style={S.guideGrid}>
                        {[
                            { step: 1, icon: Search, color: '#6366f1', bg: '#eef2ff', title: '기공소 둘러보기', desc: '우리 지역 기공소를 찾아 프로필을 확인하세요', path: '/clinic/find-lab' },
                            { step: 2, icon: ClipboardList, color: '#059669', bg: '#ECFDF5', title: 'A/S 재제작 상담 의뢰', desc: '기공소를 선택해 재제작 상담을 신청하면 덴클이 확인 후 연결해 드립니다', path: '/as-remake' },
                            { step: 3, icon: FilePlus, color: '#D97706', bg: '#FFFBEB', title: '정식 주문 작성', desc: '연결된 기공소에 의뢰서를 작성해 거래를 시작하세요', path: '/create-order' },
                        ].map(g => {
                            const Icon = g.icon;
                            return (
                                <button key={g.step} onClick={() => navigate(g.path)}
                                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, background: g.bg, border: '1px solid #E5E7EB', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ width: 22, height: 22, borderRadius: '50%', background: g.color, color: '#fff', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{g.step}</span>
                                        <Icon size={16} color={g.color} />
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{g.title}</span>
                                    </div>
                                    <span style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.6 }}>{g.desc}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* 도착 예정 칩 카드 — 기공소 대시보드의 '오늘/내일 마감' 칩 시각을 치과 관점으로 이식 */}
            <div style={S.arriveGrid}>
                {/* 오늘 = 오늘 예정 건만 (2026-09-29 대표 확정 — 예정일 지난 건·수동 도착 기록 건은 합치지 않음). 버튼은 [3D]·[리메이크]만 */}
                <ArriveCard
                    title="오늘 도착 예정"
                    list={groups.today.filter(o => hitQ(o.patientName))}
                    chipStyle={{ backgroundColor: 'rgba(208,59,59,0.08)', borderColor: 'rgba(208,59,59,0.35)' }}
                    dotColor="#d03b3b" nameColor="#d03b3b" onView={(id) => navigate('/view-order/' + id)}
                    onRemake={goRemake} onPreview={open3d}
                />
                <ArriveCard
                    title="내일 도착 예정" list={groups.tomorrow.filter(o => hitQ(o.patientName))}
                    chipStyle={{ backgroundColor: '#fffbeb', borderColor: '#fde68a' }}
                    dotColor="#f59e0b" nameColor="#0f172a" onView={(id) => navigate('/view-order/' + id)}
                    onRemake={goRemake}
                />
            </div>
            {groups.noDate.length > 0 && (
                <div style={{ ...S.card, marginTop: 0 }}>
                    <ArriveCard
                        title="도착일을 안 적은 건" list={groups.noDate.filter(o => hitQ(o.patientName)).slice(0, 30)} bare
                        chipStyle={{ backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}
                        dotColor="#94a3b8" nameColor="#334155" onView={(id) => navigate('/view-order/' + id)}
                        onRemake={goRemake}
                    />
                </div>
            )}

            {/* '오늘 완료됨' 블록은 2026-10-07 대표 승인(단순화)으로 제거 — 완료는 달력의 회색 '완료'로만 보인다 */}

            {/* 작업 공정 — 월별 캘린더 (2026-09-29 대표 지시). 3단계 숫자 요약 줄은 2026-10-12 삭제(대표 "권고대로", 앱 1.3.0과 동일).
                스캔만 있는 건(의뢰서 없음)은 납기가 없어 캘린더에 못 올리므로 접기 버튼으로 둔다(플래그 clinicScanIntake ON일 때만 조회됨) */}
            <div style={S.card}>
                <h3 style={S.h3}>작업 공정</h3>
                <div style={{ ...S.sumRow, marginTop: 8 }}>
                    {(() => {
                        // 보이는 스캔만 건이 0이면 "스캔만 0건"을 띄우지 않는다 — 숨김 건만 있을 때는 "숨김 N건 보기"만 (2026-09-29 검토 P2)
                        const nVis = scanOnly.filter(sc => hitQ(scanNameOf(sc))).length;
                        if (nVis > 0) return (
                            <button onClick={() => setShowScanFold(v => !v)} style={{ ...S.linkBtn, marginLeft: 'auto' }}>
                                스캔만 {nVis}건 {showScanFold ? '▾' : '▸'}
                            </button>
                        );
                        if (scanHidden.length > 0) return (
                            <button onClick={() => { setShowScanFold(v => !v); setShowHiddenScans(true); }} style={{ ...S.linkBtn, marginLeft: 'auto' }}>
                                숨김 {scanHidden.length}건 {showScanFold ? '접기' : '보기'}
                            </button>
                        );
                        return null;
                    })()}
                </div>
                {showScanFold && (scanOnly.length > 0 || scanHidden.length > 0) && (
                    <div style={S.scanFold}>
                        <div style={{ ...S.chipRow, marginTop: 6 }}>
                            {scanOnly.filter(sc => hitQ(scanNameOf(sc))).map(sc => (
                                <div key={'scan-' + sc.id} onClick={() => navigate('/create-order?scanId=' + sc.id)}
                                    title="의뢰서 작성"
                                    style={{ ...S.arriveChip, backgroundColor: '#0ea5e914', borderColor: '#0ea5e955', borderStyle: 'dashed' }}>
                                    <span style={{ ...S.chipDot, backgroundColor: '#0ea5e9' }} />
                                    <span style={{ ...S.chipName, color: '#0f172a' }}>{scanNameOf(sc)}</span>
                                    <span style={{ fontSize: 11, color: '#0ea5e9', fontWeight: 700 }}>의뢰서 작성</span>
                                    <button onClick={(e) => { e.stopPropagation(); toggleScanHidden(sc, true); }} title="이 스캔 숨기기 (되살릴 수 있음)" style={S.btnX}>×</button>
                                </div>
                            ))}
                            {scanOnly.length === 0 && <span style={S.sub}>없음</span>}
                        </div>
                        {scanHidden.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <button onClick={() => setShowHiddenScans(v => !v)} style={S.linkBtn}>
                                    숨김 {scanHidden.length}건 · {showHiddenScans ? '접기' : '보기'}
                                </button>
                                {showHiddenScans && (
                                    <div style={{ ...S.chipRow, marginTop: 6 }}>
                                        {scanHidden.map(sc => (
                                            <div key={'hid-' + sc.id} style={{ ...S.arriveChip, backgroundColor: '#f1f5f9', borderColor: '#e2e8f0', cursor: 'default', opacity: 0.8 }}>
                                                <span style={{ ...S.chipName, color: '#64748b', textDecoration: 'line-through' }}>{scanNameOf(sc)}</span>
                                                <button onClick={() => toggleScanHidden(sc, false)} style={S.btnUndo}>되살리기</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <ClinicProcessCalendar
                    orders={orders} qNorm={qNorm}
                    onView={(id) => navigate('/view-order/' + id)} onPreview={open3d}
                    escDisabled={!!preview3d}
                />
            </div>

            {/* 하단 도착 예정 표 제거 (2026-09-02 대표 지시). 도착완료/되돌리기 버튼도 제거 (2026-09-29 대표 확정) */}
            {/* [리메이크] → 재제작/수리 선택 (2026-09-04) */}
            <RemakeKindModal order={remakePick} onPick={(k) => goRemakeWithKind(remakePick, k)} onClose={() => setRemakePick(null)} />
            {preview3d && (
                <StlViewer entries={preview3d.entries} title={preview3d.title} notice={preview3d.notice} onClose={() => setPreview3d(null)} />
            )}

        </div>
    );
}

// 도착 예정 칩 카드 — 환자 1명 = 칩 1개, flex-wrap 횡렬 (기공소 DeadlineCard 시각 재현)
//  칩 옆 버튼은 [3D]·[리메이크]만 — [도착완료]/[되돌리기]는 2026-09-29 대표 확정으로 제거
const ArriveCard = ({ title, list, chipStyle, dotColor, nameColor, onView, onRemake, onPreview, bare }) => {
    const body = (
        <>
            <div style={S.arriveHead}>
                <span style={S.h3}>{title}</span>
                <span style={S.arriveCount}>{list.length}건</span>
            </div>
            {list.length === 0 ? (
                <div style={S.sub}>해당하는 건이 없습니다.</div>
            ) : (
                <div style={S.chipRow}>
                    {list.map((o, idx) => {
                        return (
                            <div key={'ar-' + idx + '-' + o.id} onClick={() => onView(o.id)}
                                style={{ ...S.arriveChip, ...chipStyle }}>
                                <span style={{ ...S.chipDot, backgroundColor: dotColor }} />
                                <span style={{ ...S.chipName, color: nameColor }}>{o.patientName || '환자명 없음'}</span>
                                <span style={S.chipPartner}>{'· ' + (o.toUserName || '기공소')}</span>
                                {(() => {   // 공정 3단계 — 읽기 전용(누르면 칩 전체가 의뢰서 보기). 판정 = 보낸 의뢰서·기공 캘린더와 같은 clinicStageOf
                                    const st = clinicStageOf(o);
                                    const label = st.label || '접수됨';
                                    const fg = st.key && !st.autoDue ? STAGE3_COLOR[st.key] : '#94a3b8';
                                    return <span title={st.detail || label} style={{ ...S.chipStage, background: fg + '14', color: fg }}>{label}</span>;
                                })()}
                                {o.isRemake && (() => { const c = remakeBadgeColors(o); return (
                                    <span style={{ ...S.chipRemake, background: c.bg, color: c.fg }}>{remakeLabelOf(o, '재제작')}</span>
                                ); })()}
                                {onPreview && o.designPreview?.files?.length > 0 && (
                                    <button onClick={(e) => { e.stopPropagation(); onPreview(o); }} style={S.btn3d} title="3D 디자인 미리보기">3D</button>
                                )}
                                {onRemake && (
                                    <button onClick={(e) => { e.stopPropagation(); onRemake(o); }} style={S.btnUndo} title="이 의뢰서를 불러와 재제작 의뢰서 작성">리메이크</button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
    return bare ? body : <div style={S.card}>{body}</div>;
};

const S = {
    page: { padding: 20, maxWidth: 1120, margin: '0 auto' },
    topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '4px 0 16px', flexWrap: 'wrap' },
    topText: { fontSize: 12.5, color: '#64748b', flex: 1, minWidth: 260, lineHeight: 1.6 },
    chip: { padding: '7px 13px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
    btnP: { padding: '8px 15px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    error: { background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '11px 13px', fontSize: 13, marginBottom: 12 },
    empty: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 26, textAlign: 'center', fontSize: 14, color: '#9ca3af', background: '#ffffff' },

    guide: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px', marginBottom: 16 },
    guideTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 12 },
    guideGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 },

    arriveGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 14 },
    arriveHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
    arriveCount: { fontSize: 12, fontWeight: 700, color: '#64748b' },
    chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    arriveChip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 999, cursor: 'pointer', border: '1px solid', fontSize: 13, fontFamily: 'inherit' },
    chipDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
    chipName: { fontWeight: 700 },
    chipPartner: { color: '#94a3b8', fontWeight: 500 },
    chipStage: { fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 },
    chipRemake: { fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap' },   // 재제작/수리/수정 배지

    sumRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 10, fontSize: 13, fontWeight: 700 },
    scanFold: { border: '1px dashed #cbd5e1', borderRadius: 8, padding: '10px 12px', marginBottom: 10, background: '#fbfcfd' },

    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px', marginBottom: 14 },
    h3: { fontSize: 13.5, margin: '0 0 3px', fontWeight: 700, color: '#0f172a' },
    cap: { fontSize: 11.5, color: '#64748b', margin: '0 0 14px', lineHeight: 1.5 },
    sub: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6 },
    sub2: { fontSize: 10.5, color: '#94a3b8', fontWeight: 500, marginTop: 2 },

    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', padding: '8px 10px', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '12px 10px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    dim: { opacity: 0.6 },
    trLate: { background: '#fffafa' },
    steps: { display: 'inline-flex', alignItems: 'center', gap: 3, verticalAlign: 'middle' },
    stepDot: { width: 7, height: 7, borderRadius: '50%', display: 'block' },
    badge: { fontSize: 12, fontWeight: 700, borderRadius: 7, padding: '5px 10px', whiteSpace: 'nowrap', display: 'inline-block' },
    btnX: { marginLeft: 2, width: 18, height: 18, lineHeight: '16px', padding: 0, borderRadius: '50%', border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' },
    linkBtn: { background: 'none', border: 'none', padding: 0, color: '#64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' },
    btn3d: { marginLeft: 4, padding: '4px 9px', borderRadius: 999, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', fontSize: 11.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
    btnUndo: { marginLeft: 4, padding: '4px 10px', borderRadius: 999, border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
    btnCheck: { padding: '10px 20px', borderRadius: 9, border: 'none', background: '#0ca30c', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },
    btnMini: { padding: '6px 11px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#334155', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' },

    fold: { borderTop: '1px solid #f1f5f9', marginTop: 12, paddingTop: 10 },
    summary: { cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#334155' },
    note: { marginTop: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', fontSize: 12.5, color: '#64748b', lineHeight: 1.75 },
};

export default ClinicToday;
